import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Clock, Tag, Calendar, ArrowLeft,
  BookOpen, TrendingUp, Lightning, Users
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { BLOG_POSTS, BLOG_CATEGORIES } from '../data/contentData';

export default function InsightsPage() {
  const { slug } = useParams();
  const [selectedCategory, setSelectedCategory] = useState('all');

  // If viewing a specific post
  if (slug) {
    const post = BLOG_POSTS.find(p => p.slug === slug);
    if (!post) return <PostNotFound />;
    return <BlogPostPage post={post} />;
  }

  const filteredPosts = selectedCategory === 'all' 
    ? BLOG_POSTS 
    : BLOG_POSTS.filter(p => p.category.toLowerCase() === selectedCategory);

  const featuredPosts = BLOG_POSTS.filter(p => p.featured);

  return (
    <div className="min-h-screen transition-colors duration-300" style={{ background: "var(--background)", color: "var(--foreground)" }}>
      <Navbar />

      {/* Hero */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#00F0FF]/5 via-transparent to-transparent" />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <span className="text-xs font-mono uppercase tracking-widest text-[#00F0FF] mb-4 block">
              Insights & Resources
            </span>
            <h1 className="font-heading text-5xl md:text-7xl font-black tracking-tight mb-6">
              The Outsourcing<br />Playbook
            </h1>
            <p className="text-xl theme-text-secondary max-w-2xl mx-auto">
              Strategies for building your business, not your software. Inspired by the 4-Hour Work Week principles.
            </p>
          </motion.div>

          {/* Featured Posts */}
          <div className="grid md:grid-cols-2 gap-6 mb-16">
            {featuredPosts.slice(0, 2).map((post, i) => (
              <Link
                key={post.id}
                to={`/insights/${post.slug}`}
                className={`group relative overflow-hidden border theme-border theme-surface-el p-8 hover:border-[#00F0FF]/50 transition-all ${
                  i === 0 ? 'md:col-span-2' : ''
                }`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-xs font-mono uppercase tracking-wider text-[#00F0FF]">
                    {post.category}
                  </span>
                  <span className="text-xs theme-text-muted">{post.readTime} read</span>
                </div>
                <h2 className={`font-heading font-bold mb-4 group-hover:text-[#00F0FF] transition-colors ${
                  i === 0 ? 'text-3xl md:text-4xl' : 'text-2xl'
                }`}>
                  {post.title}
                </h2>
                <p className="theme-text-muted mb-6">{post.excerpt}</p>
                <div className="flex items-center gap-2 text-[#00F0FF] text-sm">
                  Read Article <ArrowRight size={16} />
                </div>
              </Link>
            ))}
          </div>

          {/* Categories */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 text-sm font-mono uppercase tracking-wider transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-[#00F0FF] text-black'
                  : 'border border-white/20 theme-text-secondary hover:border-[#00F0FF]'
              }`}
            >
              All Posts
            </button>
            {BLOG_CATEGORIES.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 text-sm font-mono uppercase tracking-wider transition-colors ${
                  selectedCategory === cat.id
                    ? 'bg-[#00F0FF] text-black'
                    : 'border border-white/20 theme-text-secondary hover:border-[#00F0FF]'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Posts Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPosts.map((post, i) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <Link
                  to={`/insights/${post.slug}`}
                  className="group block border theme-border theme-surface-el p-6 hover:border-[#00F0FF]/50 transition-all h-full"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xs font-mono uppercase text-[#00F0FF]">{post.category}</span>
                    <span className="text-xs theme-text-muted flex items-center gap-1">
                      <Clock size={12} /> {post.readTime}
                    </span>
                  </div>
                  <h3 className="font-bold text-lg mb-3 group-hover:text-[#00F0FF] transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-sm theme-text-muted mb-4">{post.excerpt}</p>
                  <div className="flex flex-wrap gap-2">
                    {post.tags.slice(0, 3).map(tag => (
                      <span key={tag} className="text-xs px-2 py-1 bg-[var(--input-bg)] theme-text-muted">
                        #{tag}
                      </span>
                    ))}
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-24 theme-surface">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <BookOpen size={48} className="text-[#00F0FF] mx-auto mb-6" />
          <h2 className="font-heading text-3xl font-bold mb-4">
            Get the Outsourcing Playbook
          </h2>
          <p className="theme-text-secondary mb-8">
            Weekly insights on building your business while outsourcing the rest. Join 5,000+ founders.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 bg-[var(--input-bg)] border theme-border px-4 py-3 focus:border-[#00F0FF] focus:outline-none"
            />
            <Button className="bg-[#00F0FF] text-black font-bold px-6">
              Subscribe
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

// Individual Blog Post Page
function BlogPostPage({ post }) {
  const relatedPosts = BLOG_POSTS.filter(p => 
    p.id !== post.id && 
    (p.category === post.category || p.tags.some(t => post.tags.includes(t)))
  ).slice(0, 3);

  return (
    <div className="min-h-screen transition-colors duration-300" style={{ background: "var(--background)", color: "var(--foreground)" }}>
      <Navbar />

      <article className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6">
          {/* Back Link */}
          <Link 
            to="/insights" 
            className="inline-flex items-center gap-2 text-[#00F0FF] font-mono text-sm mb-8"
          >
            <ArrowLeft size={16} /> Back to Insights
          </Link>

          {/* Header */}
          <header className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <span className="text-xs font-mono uppercase tracking-wider px-3 py-1 bg-[#00F0FF]/20 text-[#00F0FF]">
                {post.category}
              </span>
              <span className="text-sm theme-text-muted flex items-center gap-2">
                <Clock size={14} /> {post.readTime} read
              </span>
              <span className="text-sm theme-text-muted flex items-center gap-2">
                <Calendar size={14} /> {new Date(post.publishedAt).toLocaleDateString('en-US', { 
                  month: 'long', 
                  day: 'numeric', 
                  year: 'numeric' 
                })}
              </span>
            </div>

            <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tight mb-6">
              {post.title}
            </h1>

            <p className="text-xl theme-text-secondary">
              {post.excerpt}
            </p>
          </header>

          {/* Content */}
          <div className="prose prose-invert prose-lg max-w-none">
            <div 
              className="blog-content"
              dangerouslySetInnerHTML={{ 
                __html: post.content
                  .replace(/^# /gm, '<h1 class="font-heading text-3xl font-bold mt-12 mb-6">')
                  .replace(/^## /gm, '<h2 class="font-heading text-2xl font-bold mt-10 mb-4">')
                  .replace(/^### /gm, '<h3 class="font-heading text-xl font-bold mt-8 mb-3">')
                  .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>')
                  .replace(/\n\n/g, '</p><p class="theme-text-secondary mb-4">')
                  .replace(/^- (.*)/gm, '<li class="theme-text-secondary ml-4">$1</li>')
                  .replace(/^\d+\. (.*)/gm, '<li class="theme-text-secondary ml-4">$1</li>')
                  .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-[#00F0FF] hover:underline">$1</a>')
                  .replace(/✅/g, '<span class="text-[#00FF94]">✅</span>')
              }}
            />
          </div>

          {/* Tags */}
          <div className="mt-12 pt-8 border-t theme-border">
            <div className="flex flex-wrap gap-2">
              {post.tags.map(tag => (
                <span key={tag} className="text-sm px-3 py-1 bg-[var(--input-bg)] theme-text-secondary">
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 p-8 border border-[#00F0FF]/30 bg-[#00F0FF]/5">
            <h3 className="font-heading text-2xl font-bold mb-4">
              Ready to Outsource Your Development?
            </h3>
            <p className="theme-text-secondary mb-6">
              Talk to our AI-powered requirement engine and get an instant project evaluation.
            </p>
            <Link to="/start-project">
              <Button className="bg-[#00F0FF] text-black font-bold px-6">
                Start Your Project <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="max-w-7xl mx-auto px-6 mt-24">
            <h2 className="font-heading text-2xl font-bold mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map(relPost => (
                <Link
                  key={relPost.id}
                  to={`/insights/${relPost.slug}`}
                  className="group border theme-border theme-surface-el p-6 hover:border-[#00F0FF]/50 transition-all"
                >
                  <span className="text-xs font-mono text-[#00F0FF]">{relPost.category}</span>
                  <h3 className="font-bold mt-2 mb-2 group-hover:text-[#00F0FF] transition-colors">
                    {relPost.title}
                  </h3>
                  <p className="text-sm theme-text-muted">{relPost.readTime} read</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>

      <Footer />
    </div>
  );
}

function PostNotFound() {
  return (
    <div className="min-h-screen transition-colors duration-300" style={{ background: "var(--background)", color: "var(--foreground)" }}>
      <Navbar />
      <div className="py-32 text-center">
        <h1 className="font-heading text-4xl font-bold mb-4">Post Not Found</h1>
        <p className="theme-text-muted mb-8">The article you're looking for doesn't exist.</p>
        <Link to="/insights">
          <Button>Back to Insights</Button>
        </Link>
      </div>
      <Footer />
    </div>
  );
}
